"use client";

import { useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { messagePartage, parametresCarte } from "@/lib/tools/partage";
import { copierMessage, partagerSituation } from "./partagerSituation";

/**
 * « Partager ma carte » : la situation du lecteur en image, sans un seul
 * montant (app/api/og/situation), à publier ou à envoyer à un ami.
 *
 * LE LIEN PARTAGÉ NE PORTE RIEN DU LECTEUR. Il mène à /outils, vide : celui
 * qui le reçoit tape le nom de sa propre entreprise. L'image, elle, ne dit
 * que des pourcentages, des jours et une ancienneté.
 */

const T = {
    fr: {
        kicker: "À partager",
        titre: "Qui autour de vous devrait voir ses dates ?",
        texte: "Un ami qui se lance, une collègue en micro, un associé : envoyez-leur votre carte. Ils tapent le nom de leur entreprise et voient la leur.",
        apercu: "Votre message",
        alt: "Ma carte de situation, sans montant",
        partager: "Envoyer à un ami",
        telecharger: "Télécharger",
        copier: "Copier le message",
        copie: "Message copié",
        garantie: "La carte et le message ne montrent aucun montant : des dates et des pourcentages. Le lien mène à l'outil, vide.",
        fichier: "ma-situation.png",
    },
    en: {
        kicker: "To share",
        titre: "Who around you should see their dates?",
        texte: "A friend starting out, a colleague on micro, a partner: send them your card. They type their company's name and see their own.",
        apercu: "Your message",
        alt: "My situation card, without amounts",
        partager: "Send to a friend",
        telecharger: "Download",
        copier: "Copy the message",
        copie: "Message copied",
        garantie: "The card and the message show no amounts: dates and percentages only. The link leads to the tool, empty.",
        fichier: "my-situation.png",
    },
};

export default function ToolsShareCard({ lang = "fr", r, part }) {
    const t = T[lang] || T.fr;
    const [confirme, setConfirme] = useState(false);
    const image = `/api/og/situation?${parametresCarte(r, part, lang)}`;
    const message = messagePartage(r, lang).texte;

    const signalerCopie = () => {
        setConfirme(true);
        setTimeout(() => setConfirme(false), 2500);
    };
    const copier = async () => {
        await copierMessage({ r, lang });
        signalerCopie();
    };
    const partager = async () => {
        if ((await partagerSituation({ r, part, lang })) === "copie") signalerCopie();
    };

    const pastille =
        "inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors duration-200";
    return (
        /* LA CARTE COMME UN OBJET : penchée comme le tampon de /outils, elle
           sort de son cadre (en haut et en bas, dès la tablette) et se redresse
           quand la souris la prend, sur une lueur orange. Les trois actions
           tiennent sur une ligne, et « Lien copié » s'affiche sur le bouton. */
        <section
            className="relative rounded-2xl border border-gray-200/80 dark:border-white/10 bg-gradient-to-br from-white via-white to-[#fff4ee] dark:from-white/[0.04] dark:via-white/[0.03] dark:to-[#D04515]/10 p-5 sm:p-7 md:!mt-20 md:!mb-12"
            aria-labelledby="carte-titre"
        >
            <div className="grid gap-6 sm:gap-8 md:grid-cols-[minmax(0,230px)_1fr] md:items-center">
                <div className="relative flex justify-center md:-my-16 md:block">
                    <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D04515]/25 blur-3xl" />
                    {/* eslint-disable-next-line @next/next/no-img-element -- image dessinée à la volée, déjà à la bonne taille */}
                    <img
                        src={image}
                        alt={t.alt}
                        width={1080}
                        height={1350}
                        loading="lazy"
                        className="relative h-auto w-full max-w-[210px] -rotate-3 rounded-2xl shadow-[0_30px_60px_-18px_rgba(17,24,39,0.6)] ring-1 ring-black/5 transition-transform duration-300 ease-out hover:rotate-0 hover:scale-[1.03] md:max-w-[230px] motion-reduce:rotate-0 motion-reduce:transition-none"
                    />
                </div>
                <div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-[#B83C12] dark:text-[#FF8A5B]">
                        <SolarIcon name="gift-linear" className="h-3.5 w-3.5" />
                        {t.kicker}
                    </span>
                    <h2 id="carte-titre" className="mt-3 mb-2 text-xl sm:text-2xl font-bold font-sourceSans tracking-tight dark:text-white">
                        {t.titre}
                    </h2>
                    <p className="mb-4 text-[15px] leading-6 text-gray-600 dark:text-lightBlue">{t.texte}</p>
                    {/* LE MESSAGE, MONTRÉ AVANT D'ÊTRE ENVOYÉ : la découverte
                        la plus parlante de la situation, au tutoiement, sans
                        un montant. Voir ce qu'on enverra donne envie de
                        l'envoyer. */}
                    <figure className="mb-5">
                        <figcaption className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-lightBlue/70">{t.apercu}</figcaption>
                        <blockquote className="m-0 max-w-md rounded-2xl rounded-bl-md bg-[#D04515] px-4 py-2.5 text-[15px] leading-6 text-white shadow-sm">
                            {message} <span className="underline decoration-white/50 underline-offset-2">{lang === "en" ? "believemy.com/en/tools" : "believemy.com/outils"}</span>
                        </blockquote>
                    </figure>
                    <div className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={partager} className={`${pastille} border-[#D04515] bg-[#D04515] text-white hover:bg-[#B83C12]`}>
                            <SolarIcon name="share-linear" className="h-4 w-4" />
                            {t.partager}
                        </button>
                        <a href={image} download={t.fichier} className={`${pastille} border-gray-200/80 dark:border-white/10 bg-white/80 dark:bg-white/[0.04] text-gray-800 dark:text-white hover:border-gray-300 hover:!text-gray-900`}>
                            <SolarIcon name="download-minimalistic-linear" className="h-4 w-4 text-gray-500 dark:text-lightBlue/70" />
                            {t.telecharger}
                        </a>
                        <button
                            type="button"
                            onClick={copier}
                            className={`${pastille} ${
                                confirme
                                    ? "border-green-600/40 bg-green-50 text-green-800 dark:bg-green-500/10 dark:text-green-300"
                                    : "border-gray-200/80 dark:border-white/10 bg-white/80 dark:bg-white/[0.04] text-gray-800 dark:text-white hover:border-gray-300"
                            }`}
                        >
                            <SolarIcon
                                name={confirme ? "check-circle-bold" : "copy-linear"}
                                className={`h-4 w-4 ${confirme ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-lightBlue/70"}`}
                            />
                            {confirme ? t.copie : t.copier}
                        </button>
                    </div>
                    <p className="mt-3 mb-0 flex items-start gap-1.5 text-xs text-gray-500 dark:text-lightBlue/70">
                        <SolarIcon name="lock-keyhole-minimalistic-linear" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {t.garantie}
                    </p>
                    <p aria-live="polite" className="sr-only">
                        {confirme ? t.copie : ""}
                    </p>
                </div>
            </div>
        </section>
    );
}
